import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMyAccountData } from "../../../hooks/useMyAccountData";
import {
	lookupABN,
	lookupACN,
	type ABNLookupResult,
	type ACNLookupResult,
} from "../../../services/abnLookupService";
import { type EntityCodeType } from "../../../services/registrationApi";
import { socketService } from "../../../services/socketService";
import wsApi, {
	type RiskAssessmentAnswer,
	type RiskLevel,
} from "../../../services/wsApi";
import { logger } from "../../../utils/logger";

import type {
	C1TypeId,
	C2HistoryEntry,
	C2TypeId,
	Question,
	RiskRating,
} from "../../../types/risk-assessment.types";

import {
	C1_TYPES,
	C2_TYPES,
	RISK_CFG,
	buildQuestions,
	computeRisk,
} from "../../../data/riskAssessmentData";

import {
	STORAGE_KEY_ASSESSMENTS,
	mapBackendRiskLevel,
} from "../../../utils/assessmentHelpers";
import {
	RISK_RATING_TO_WIRE,
	UnknownRiskRatingError,
	parseWireRiskLevelToTitle
} from "../../../utils/riskRating";

// ─── Types ──────────────────────────────────────────────────────────────────────

export type PageView = "assessment" | "history";

export type EntitySubType =
	| "sole-trader"
	| "company"
	| "partnership"
	| "trust"
	| "association"
	| "government"
	| "";

// ─── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Resolve the C1 profession type from account data.
 *
 * Any recognised value (including the catch-all `"other"`, and any account
 * that has loaded but doesn't match a specific sector) resolves to a
 * concrete `C1TypeId` so the risk assessment screen doesn't force a
 * manual selection. `null` is only returned when `accountData` is not yet
 * loaded — the caller uses that to render a genuine "please select"
 * prompt while data is in flight.
 *
 * `"other"` — and any account whose `reportingEntityType` /
 * `industryDescription` / `services` / `sector` don't specifically match
 * one of the AUSTRAC designated-service categories — resolves to
 * `"accountant"`, since accounting questions are the broadest set and
 * cover the majority of "other reporting entity" cases in practice.
 *
 * Exported for unit testing.
 */
export function detectC1Type(accountData: any): C1TypeId | null {
	// 1. Exact match on reportingEntityType (most reliable)
	const ret = (accountData?.reportingEntityType || "").toLowerCase();
	if (ret === "accounting") return "accountant";
	if (ret === "legal") return "legal";
	if (ret === "conveyancing") return "conveyancer";
	if (ret === "real-estate") return "real_estate";
	if (ret === "precious-metals" || ret === "bullion" || ret === "bullion_dealer") return "bullion_dealer";
	// Match "other" in any form: exact, "other-services", "other/misc", etc.
	if (ret.includes("other")) return "accountant";

	// 2. industryDescription — TSU uses same questions as accounting
	const industryDesc = (accountData?.industryDescription || "").toLowerCase();
	if (industryDesc === "tsu" || industryDesc.includes("trust") || industryDesc.includes("company service"))
		return "accountant";
	if (industryDesc.includes("other")) return "accountant";

	// 3. services field
	const industry = (accountData?.services || "").toLowerCase();
	if (industry.includes("accountant") || industry.includes("accounting") || industry.includes("bookkeep"))
		return "accountant";
	if (industry.includes("legal") || industry.includes("law") || industry.includes("solicitor") || industry.includes("barrister"))
		return "legal";
	if (industry.includes("conveyancer") || industry.includes("conveyancing"))
		return "conveyancer";
	if (industry.includes("real estate") || industry.includes("real-estate") || industry.includes("property agent"))
		return "real_estate";
	if (industry.includes("bullion") || industry.includes("precious metal") || industry.includes("gold dealer"))
		return "bullion_dealer";
	if (industry === "tsu" || industry.includes("trust") || industry.includes("company service"))
		return "accountant";
	if (industry.includes("other")) return "accountant";

	// 4. Sector fallback — intentionally narrow to avoid false "accountant" matches
	const sector = (accountData?.sector || "").toLowerCase();
	if (sector === "accounting" || sector === "accountant")
		return "accountant";
	if (sector === "legal" || sector === "law")
		return "legal";
	if (sector === "conveyancing" || sector === "conveyancer")
		return "conveyancer";
	if (sector === "real-estate" || sector === "real estate")
		return "real_estate";
	if (sector === "bullion" || sector === "precious-metals" || sector === "bullion_dealer")
		return "bullion_dealer";
	if (sector === "tsu")
		return "accountant";
	if (sector.includes("other")) return "accountant";

	// 5. Defensive fallback — if account data has loaded (organisation name
	// present) but nothing above matched, treat as "other" and default to
	// accountant. This covers accounts where the backend records a sector
	// value the portal doesn't know about, or omits it entirely, so the
	// user isn't stuck on the "please select below" warning.
	const hasLoadedData = !!(accountData?.organisation || accountData?.abn || accountData?.accountId);
	if (hasLoadedData) return "accountant";

	// Genuinely no data yet — caller should keep waiting.
	return null;
}

function mapBackendToHistoryEntry(a: any): C2HistoryEntry {
	return {
		id: a.assessmentId,
		c1Type: (a.profession?.toLowerCase().includes("legal")
			? "legal"
			: a.profession?.toLowerCase().includes("convey")
				? "conveyancer"
				: a.profession?.toLowerCase().includes("real estate")
					? "real_estate"
					: "accountant") as C1TypeId,
		c2Name: a.clientName || a.c2OrganizationId || "Unknown",
		/**
		 * AL-420 §2 / AL-351: read the stored, registry-derived type. The name
		 * heuristic that used to stand in for it is gone.
		 *
		 * It could not produce `sole_trader` or `government` at all, collapsed
		 * partnerships, LLPs and co-ops to `individual`, and defaulted anything
		 * unrecognised to `individual` — which carries `hasOwnership: false` and so
		 * silently dropped `cr_own` and the ownership-dependent country question for
		 * entities that genuinely have an ownership structure. A wrong answer to a
		 * risk question is worse than a missing one.
		 *
		 * Null for pre-AL-351 rows, by deliberate decision — no backfill. Rendered
		 * as "not recorded" rather than guessed.
		 */
		c2EntityType: (a.c2EntityType || null) as C2TypeId | null,
		/**
		 * AL-442 #5: a nested ternary whose final branch returned "Low" for
		 * anything unrecognised — the same silent-downgrade as #1, on the resume
		 * path. Now a strict parse that throws.
		 */
		riskRating: parseWireRiskLevelToTitle(a.riskLevel),
		// AL-350: the summary list no longer carries `answers` — the server computes
		// both counts in SQL and sends them on every row. Without reading them here
		// the history cards would render "0/0 flagged" for every assessment.
		//
		// `??` rather than `||` is deliberate: a genuine server-supplied 0 (an
		// assessment with nothing flagged) must be kept, not treated as absent and
		// recomputed. The `a.answers` fallback keeps this correct against a
		// pre-AL-350 server, where the counts are absent but the answers are present.
		flaggedCount:
			a.flaggedCount ??
			a.answers?.filter((ans: any) => ans.answer === "Yes").length ??
			0,
		totalQuestions: a.totalQuestions ?? a.answers?.length ?? 0,
		completedAt: a.updatedAt || a.createdAt || new Date().toISOString(),
		createdByName: a.createdByName?.trim() || undefined,
		createdBy: a.createdBy || undefined,
		status: a.status,
		answers:
			a.answers?.reduce(
				(acc: Record<string, boolean>, ans: any) => {
					acc[ans.questionId] = ans.answer === "Yes";
					return acc;
				},
				{} as Record<string, boolean>,
			) || {},
	};
}

/**
 * AL-442 / AL-420: map one row, dropping it rather than taking the list down
 * with it.
 *
 * `mapBackendToHistoryEntry` throws on an unrecognised `riskLevel`, which is the
 * right behaviour — AL-442 exists because the old nested ternary silently
 * returned "Low" for anything it did not know, and a wrong risk rating is worse
 * than a missing one.
 *
 * But the three callers all map a whole list, and two of them do it inside a
 * try/catch that only logs. One unrecognised row therefore aborted the entire
 * `.map`, `setHistory` never ran, and the C1 saw an EMPTY history — indistinguishable
 * from an organisation that has never been assessed. The third caller, the
 * `RiskAssessmentCreated` socket handler, had no guard at all and would throw
 * inside the event handler.
 *
 * That is the same trap AL-442 already reasoned about for `mapHistoryToAssessment`,
 * which returns null so a bad localStorage entry cannot empty the whole history.
 * This applies the identical rule to the wire path.
 *
 * Only `UnknownRiskRatingError` is absorbed. Anything else is a genuine defect in
 * the mapper and is allowed to propagate — a blanket catch here would hide it.
 */
export function mapBackendToHistoryEntrySafe(a: any): C2HistoryEntry | null {
	try {
		return mapBackendToHistoryEntry(a);
	} catch (err) {
		if (err instanceof UnknownRiskRatingError) {
			// Named loudly: this row is now invisible to the C1, so the reason it
			// vanished has to be recoverable from the logs.
			logger.warn(
				"[RiskAssessment] dropping history row with unrecognised risk level",
				{ assessmentId: a?.assessmentId, riskLevel: a?.riskLevel, err },
			);
			return null;
		}
		throw err;
	}
}

/** Check if a string contains any of the given keywords (case-insensitive) */
function containsAny(text: string | undefined, keywords: string[]): boolean {
	const lower = (text || "").toLowerCase();
	return keywords.some((kw) => lower.includes(kw));
}

/** Map KYB austracType / businessType → entitySubType + c2EntityType */
function mapKybToEntityType(
	austracType: string | undefined,
	businessType: string | undefined,
	entityName?: string,
): { subType: EntitySubType; c2Type: C2TypeId } {
	const raw = (austracType || businessType || "").toLowerCase();
	// NOTE: this mapper is only called from an ABN/ACN lookup. A natural person
	// who holds an ABN is a SOLE TRADER, not a plain individual — the register
	// often reports a sole trader's type as just "Individual". Treat that (and
	// any explicit sole-trader wording) as sole_trader so the ABN is preserved
	// and the flow is not downgraded to the individual (no-ABN) path.
	if (raw === "individual" || containsAny(raw, ["sole trader", "sole_trader"]))
		return { subType: "sole-trader", c2Type: "sole_trader" };
	if (containsAny(raw, ["trust"]))
		return { subType: "trust", c2Type: "trust" };
	if (containsAny(raw, ["partner"]))
		return { subType: "partnership", c2Type: "body_corporate" };
	if (containsAny(raw, ["association", "co-operative", "cooperative"]))
		return { subType: "association", c2Type: "body_corporate" };
	if (containsAny(raw, ["government", "statutory"]))
		return { subType: "government", c2Type: "government" };
	// Check entity name for trust keywords when austracType is generic (e.g. "Body Corporate")
	if (containsAny(entityName, ["trust", "trustee"]))
		return { subType: "trust", c2Type: "trust" };
	return { subType: "company", c2Type: "body_corporate" };
}

/** Store the question wording and score for audit; the UI deliberately does not read them. */
export function buildBackendAnswers(
	questions: Question[],
	answers: Record<string, boolean>,
): RiskAssessmentAnswer[] {
	return questions.map((q) => ({
		questionId: q.id,
		questionText: q.text,
		answer:
			answers[q.id] === true
				? "Yes"
				: answers[q.id] === false
					? "No"
					: "Unanswered",
		score: answers[q.id] === true ? (q.rating === "High" ? 2 : 1) : 0,
	}));
}

// ─── Hook ───────────────────────────────────────────────────────────────────────

export function useRiskAssessmentState() {
	const navigate = useNavigate();
	const { accountData } = useMyAccountData();
	const detectedC1Type = detectC1Type(accountData);

	// ── Page-level state ──────────────────────────────────────────────────────
	const [pageView, setPageView] = useState<PageView>("assessment");
	const [history, setHistory] = useState<C2HistoryEntry[]>([]);
	const [isLoadingHistory, setIsLoadingHistory] = useState(false);
	// Whether the first fetch has finished, successfully or not. `history.length`
	// alone cannot distinguish "no assessments" from "not fetched yet", and
	// `isLoadingHistory` starts false and stays false until the connection is
	// authenticated, so neither is sufficient to suppress a misleading count.
	const [historyLoaded, setHistoryLoaded] = useState(false);
	const [step, setStep] = useState(1);
	const [c1Type, setC1Type] = useState<C1TypeId | null>(detectedC1Type);

	// Sync c1Type when detected type changes
	useEffect(() => {
		if (detectedC1Type) setC1Type(detectedC1Type);
	}, [detectedC1Type]);

	// ── Entity / customer state ───────────────────────────────────────────────
	const [c2Name, setC2Name] = useState("");
	const [c2EntityType, setC2EntityType] = useState<C2TypeId | null>(null);
	const [isC2Individual, setIsC2Individual] = useState<boolean | null>(null);
	const [entitySubType, setEntitySubType] = useState<EntitySubType>("");
	const [c2Abn, setC2Abn] = useState("");
	const [contactFirst, setContactFirst] = useState("");
	const [contactMiddle, setContactMiddle] = useState("");
	const [contactLast, setContactLast] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [contactPhone, setContactPhone] = useState("");
	const [abnCodeType, setAbnCodeType] = useState<EntityCodeType>("ABN");
	const [abnLookupResult, setAbnLookupResult] = useState<
		ABNLookupResult | ACNLookupResult | null
	>(null);
	const [abnLookupError, setAbnLookupError] = useState("");
	const [isLookingUp, setIsLookingUp] = useState(false);

	// ── Assessment state ──────────────────────────────────────────────────────
	const [answers, setAnswers] = useState<Record<string, boolean>>({});
	const [backendAssessmentId, setBackendAssessmentId] = useState<string | null>(
		null,
	);
	// AL-351: lastSaved / showSavedMsg / isSaving removed with draft saving. They
	// existed only to drive the autosave status indicator.
	const [editingId, setEditingId] = useState<string | null>(null);
	const [activeSection, setActiveSection] = useState(0);
	const [isAssessmentCompleted, setIsAssessmentCompleted] = useState(false);
	// Guards handleComplete against re-entry. The Complete button performs a
	// create followed by a history refetch, and nothing previously prevented a
	// second press during that window — each press with no assessment id takes
	// the create branch, so a double-click wrote two assessments.
	//
	// The guard is a ref, not the state below. Two clicks in the same tick would
	// both read a state value of false and both proceed, which is the same
	// staleness that caused the null assessmentId. A ref updates synchronously.
	// `isCompleting` state exists only to drive the button's disabled appearance.
	const completingRef = useRef(false);
	const [isCompleting, setIsCompleting] = useState(false);
	const [hoveredInfo, setHoveredInfo] = useState<string | null>(null);

	// ── Computed values ───────────────────────────────────────────────────────
	const selC2 = C2_TYPES.find((t) => t.id === c2EntityType) || null;
	const questions = c1Type && selC2 ? buildQuestions(c1Type, selC2) : [];
	const answered = questions.filter((q) => answers[q.id] !== undefined).length;
	const allDone = questions.length > 0 && answered === questions.length;
	const risk = allDone ? computeRisk(questions, answers) : null;
	const rc = risk ? RISK_CFG[risk] : null;
	const flagged = questions.filter((q) => answers[q.id] === true);
	const c1Label = C1_TYPES.find((t) => t.id === c1Type)?.label || "";

	// Live risk computation for dynamic indicator (works with partial answers)
	const liveHigh = questions.filter(
		(q) => answers[q.id] === true && q.rating === "High",
	).length;
	const liveMed = questions.filter(
		(q) => answers[q.id] === true && q.rating === "Medium",
	).length;
	const liveRisk: RiskRating =
		liveHigh >= 1 ? "High" : liveMed >= 2 ? "Medium" : "Low";
	const liveRc = RISK_CFG[liveRisk];
	const liveFlagged = flagged.length;
	const liveSafe = answered - liveFlagged;
	const liveUnanswered = questions.length - answered;

	const grouped = questions.reduce<Record<string, Question[]>>((a, q) => {
		if (!a[q.category]) a[q.category] = [];
		a[q.category].push(q);
		return a;
	}, {});

	// ── History loading ───────────────────────────────────────────────────────
	const loadHistory = useCallback(async () => {
		setIsLoadingHistory(true);
		try {
			const assessments = await wsApi.riskAssessmentGetAll();
			setHistory(
				assessments
					.map(mapBackendToHistoryEntrySafe)
					.filter((e): e is C2HistoryEntry => e !== null),
			);
		} catch (err) {
			// Do not swallow silently: an empty history and a failed fetch look
			// identical on screen, so without this a broken list is indistinguishable
			// from an organisation that has no assessments.
			logger.warn("[RiskAssessment] history load failed", err);
		} finally {
			setIsLoadingHistory(false);
			setHistoryLoaded(true);
		}
	}, []);

	// Load once the connection is AUTHENTICATED, not merely open.
	//
	// This effect previously called loadHistory() unconditionally on mount, which
	// is why the tab could read "History (0)" until it was clicked.
	//
	// The cause is narrower than a plain startup race. `socketService.send()`
	// transmits immediately when readyState is OPEN and queues otherwise, and
	// `markAuthenticated()` flushes that queue — so a request issued before the
	// socket opens is delivered correctly once auth completes. The failure window
	// is the one in between: socket already OPEN, `authenticateWebSocket` still in
	// flight. A GET_ALL sent there arrives unauthenticated, the lambda resolves no
	// organisation for that connection, and `getRiskAssessmentsByC1Org` returns an
	// empty list for a blank org id.
	//
	// That is a *successful* empty response. Nothing throws, the catch above never
	// runs, and no error appears anywhere — the list is simply empty until some
	// later event refetches it. Gating on `isConnected()` would not have helped
	// either, since that only tests readyState === OPEN.
	//
	// `onHealthChange` emits the current state synchronously on subscribe, so when
	// the connection is already authenticated this loads immediately rather than
	// waiting for the next transition.
	useEffect(() => {
		let loaded = false;
		return socketService.onHealthChange((health) => {
			if (!loaded && health.state === "authenticated") {
				loaded = true;
				loadHistory();
			}
		});
	}, [loadHistory]);

	// Refresh when the user opens the history tab. pageView starts on the
	// assessment view, so this does not double up with the initial load above.
	useEffect(() => {
		if (pageView === "history") loadHistory();
	}, [pageView, loadHistory]);

	// Real-time push events
	useEffect(() => {
		const handler = (data: any) => {
			const a = data?.assessment;
			if (!a) return;
			// Unguarded before: a pushed assessment carrying an unrecognised risk
			// level threw inside the socket event handler.
			const entry = mapBackendToHistoryEntrySafe(a);
			if (!entry) return;
			setHistory((prev) =>
				prev.some((h) => h.id === entry.id) ? prev : [entry, ...prev],
			);
		};
		socketService.on("RiskAssessmentCreated", handler);
		return () => {
			socketService.off("RiskAssessmentCreated", handler);
		};
	}, []);

	// AL-352 / AL-351: the `viewAssessmentId` deep link is removed alongside
	// `editHistoryEntry`.
	//
	// It read `location.state.viewAssessmentId` and loaded that assessment into
	// the editor. Nothing in the application navigated with that state — the only
	// two references were its own comment and its own read — so it was already
	// orphaned. It is removed rather than left dormant because it is the same
	// write path as `editHistoryEntry` in a different wrapper: it seeded the
	// entity type from the name-derived guess, rebuilt the question set from it,
	// and cleared both IDs so the next completion wrote a NEW row on the guessed
	// type. Wiring any "view" or "duplicate" control to it later would have
	// reintroduced exactly the defect being suppressed here.
	//
	// It also read the entity ABN back out of localStorage, which is a second
	// stale-data path with no authority behind it.

	// ── Actions ───────────────────────────────────────────────────────────────

	const reset = () => {
		setStep(1);
		setC1Type(null);
		setC2Name("");
		setC2EntityType(null);
		setIsC2Individual(null);
		setEntitySubType("");
		setC2Abn("");
		setAbnCodeType("ABN");
		setAbnLookupResult(null);
		setAbnLookupError("");
		setIsLookingUp(false);
		setAnswers({});
		setBackendAssessmentId(null);
		setEditingId(null);
		setIsAssessmentCompleted(false);
		setContactFirst("");
		setContactMiddle("");
		setContactLast("");
		setContactEmail("");
		setContactPhone("");
	};

	// ── KYB lookup ────────────────────────────────────────────────────────────

	const triggerKybLookup = async (code: string, type: EntityCodeType) => {
		if (type === "ABN" && code.length !== 11) {
			setAbnLookupError("ABN must be 11 digits");
			return;
		}
		if (type === "ACN" && code.length !== 9) {
			setAbnLookupError("ACN must be 9 digits");
			return;
		}
		if (type !== "ABN" && type !== "ACN") {
			setAbnLookupError(`Lookup not available for ${type}`);
			return;
		}

		setIsLookingUp(true);
		setAbnLookupError("");
		setAbnLookupResult(null);

		try {
			const result =
				type === "ABN" ? await lookupABN(code) : await lookupACN(code);
			if (result) {
				setAbnLookupResult(result);
				if (result.name) setC2Name(result.name);
				const { subType, c2Type } = mapKybToEntityType(
					result.austracType,
					result.businessType,
					result.name,
				);
				setEntitySubType(subType);
				setC2EntityType(c2Type);
				if (c2Type === "individual") setIsC2Individual(true);
				setAnswers({});
			} else {
				setAbnLookupError(`${type} not found in the business register`);
			}
		} catch {
			setAbnLookupError("Lookup failed — please try again");
		} finally {
			setIsLookingUp(false);
		}
	};

	const handleAbnChange = (value: string) => {
		setC2Abn(value);
		setAbnLookupResult(null);
		setAbnLookupError("");
		// No auto-lookup - user must click the Lookup button
	};

	const handleAbnCodeTypeChange = (type: EntityCodeType) => {
		setAbnCodeType(type);
		setAbnLookupResult(null);
		setAbnLookupError("");
		// No auto-lookup on type change - user must click the Lookup button
	};

	// ── Edit history ──────────────────────────────────────────────────────────

	// AL-352 / AL-351: `editHistoryEntry` removed.
	//
	// It reopened a stored assessment in the editor. Already unreachable at 4.0.2
	// — it was threaded to HistoryView as a prop but never called from its JSX,
	// so no control invoked it — and it is removed rather than left dormant
	// because of what it did when reached.
	//
	// It seeded the entity type from `entry.c2EntityType`, which for any row the
	// server did not store is the name-derived guess, then rebuilt the question
	// set from that guess. On a completed assessment it also cleared both IDs,
	// so the next completion wrote a NEW row built on the guessed type rather
	// than updating the original. That is the AL-351 defect as a write path, not
	// merely a display one, which makes it worse to leave in place than the
	// read-side heuristic removed from the CDD record view.
	//
	// A completed assessment is now terminal: it can be listed with its risk
	// rating, and not reopened.

	const deleteHistoryEntry = async (id: string) => {
		setHistory((prev) => prev.filter((h) => h.id !== id));
	};

	// ── Draft saving removed (AL-351) ─────────────────────────────────────────
	//
	// `saveDraft` and its 30-second `setInterval` were the only writers of partial
	// assessments. They are removed so an assessment is written exactly once, on
	// completion, from the question set the user actually answered.
	//
	// Why that matters: the question set is rebuilt from `c2EntityType`. Rows
	// created before AL-351 can still have no stored value because the server-side
	// schema change intentionally did not backfill historical assessments. A
	// draft round-trip on one of those rows would re-infer the type from the client
	// name, and where that guess disagrees with the real entity type the rebuilt
	// set differs from the one answered. The ownership question (`cr_own`) is then
	// added or dropped, so the stored answers no longer correspond to the
	// questions asked.
	//
	// Completing in a single pass keeps the answered set and the stored set
	// identical, because nothing re-derives the entity type in between.
	//
	// This also removes the write path that made `progress < 100` rows, so no
	// assessment can be left half-recorded.

	// ── Complete assessment ───────────────────────────────────────────────────

	const handleComplete = async () => {
		if (!risk || !c1Type || !c2EntityType) return;

		// AL-442 #6: this total map was already the only correct mapper of the six.
		// Moved to utils/riskRating so the write vocabulary sits with the read ones
		// instead of being declared inline here; behaviour is unchanged.
		const backendRiskLevel = RISK_RATING_TO_WIRE[risk] as RiskLevel;
		const c2OrganizationId =
			c2Name.toLowerCase().replace(/[^a-z0-9]/g, "_") || `c2_${Date.now()}`;

		const navState = {
			fromRiskAssessment: true,
			c2Name,
			c2EntityType,
			c2Abn,
			abnCodeType,
			entitySubType,
			riskRating: risk,
			assessmentId: null as string | null,
			contactFirst: contactFirst || undefined,
			contactMiddle: contactMiddle || undefined,
			contactLast: contactLast || undefined,
			contactEmail: contactEmail || undefined,
			contactPhone: contactPhone || undefined,
		};

		// Declared outside the try so the navigation below can read it. The id of a
		// freshly created assessment only exists here — `setBackendAssessmentId` is
		// asynchronous, so the state variable is still null for the remainder of
		// this call and cannot be used to tell the invitations page which
		// assessment it is inviting against.
		let assessmentIdToComplete: string | null = editingId || backendAssessmentId;

		if (completingRef.current) return;
		completingRef.current = true;
		setIsCompleting(true);
		const finish = () => {
			completingRef.current = false;
			setIsCompleting(false);
		};
		try {
			if (isAssessmentCompleted) {
				navState.assessmentId = assessmentIdToComplete;
				setEditingId(null);
				setBackendAssessmentId(null);
				finish();
				navigate("/portal/invitations", { state: navState });
				return;
			}

			const backendAnswers = buildBackendAnswers(questions, answers);
			const contactFullName = [contactFirst, contactMiddle, contactLast].filter(Boolean).join(" ");
			const meta = {
				clientName: c2Name,
				profession: C1_TYPES.find((t) => t.id === c1Type)?.label || c1Type,
				progress: 100,
				c2EntityType: c2EntityType || undefined,
				entityAbn: c2Abn || undefined,
				contactPerson: contactFullName || undefined,
				clientRef: contactEmail || undefined,
				contactPhone: contactPhone || undefined,
			};

			if (assessmentIdToComplete) {
				await wsApi.riskAssessmentUpdate(
					assessmentIdToComplete,
					backendRiskLevel,
					backendAnswers,
					meta,
				);
			} else {
				const saved = await wsApi.riskAssessmentCreate(
					c2OrganizationId,
					backendRiskLevel,
					backendAnswers,
					meta,
				);
				if (saved?.assessmentId) {
					setBackendAssessmentId(saved.assessmentId);
					assessmentIdToComplete = saved.assessmentId;
				}
			}

			// Sync history to localStorage
			try {
				const assessments = await wsApi.riskAssessmentGetAll();
				setHistory(
					assessments
						.map(mapBackendToHistoryEntrySafe)
						.filter((e): e is C2HistoryEntry => e !== null),
				);
				const lsEntries = assessments.map((a) => ({
					id: a.assessmentId,
					clientName: a.clientName || "",
					clientRef: a.clientRef ?? "",
					entityAbn: a.entityAbn ?? "",
					riskRating: mapBackendRiskLevel(a.riskLevel),
					savedAt: a.updatedAt ?? a.createdAt ?? new Date().toISOString(),
					profession: a.profession ?? "",
					progress: a.progress ?? 100,
					c2EntityType: a.c2EntityType ?? "",
					contactPerson: a.contactPerson ?? "",
					contactPhone: a.contactPhone ?? "",
				}));
				localStorage.setItem(
					STORAGE_KEY_ASSESSMENTS,
					JSON.stringify(lsEntries),
				);
			} catch {
				// silently fail
			}

			navState.assessmentId = assessmentIdToComplete;
		} catch (_err: any) {
			const errMsg = _err?.message || "";
			if (!errMsg.includes("completed and cannot be edited")) {
				alert("Failed to save assessment. Please try again.");
				finish();
				return;
			}
		}

		navState.assessmentId = navState.assessmentId || assessmentIdToComplete;
		setEditingId(null);
		setBackendAssessmentId(null);
		finish();
		navigate("/portal/invitations", { state: navState });
	};

	const handleLogout = () => navigate("/login");

	// ── Return ────────────────────────────────────────────────────────────────

	return {
		// Page
		pageView,
		setPageView,
		history,
		isLoadingHistory,
		historyLoaded,

		// Steps
		step,
		setStep,

		// C1
		c1Type,
		setC1Type,
		c1Label,
		detectedC1Type,

		// C2 entity
		c2Name,
		setC2Name,
		c2EntityType,
		setC2EntityType,
		isC2Individual,
		setIsC2Individual,
		entitySubType,
		setEntitySubType,
		selC2,

		// Contact person (for non-individual entities)
		contactFirst,
		setContactFirst,
		contactMiddle,
		setContactMiddle,
		contactLast,
		setContactLast,
		contactEmail,
		setContactEmail,
		contactPhone,
		setContactPhone,

		// ABN / KYB
		c2Abn,
		setC2Abn,
		abnCodeType,
		abnLookupResult,
		setAbnLookupResult,
		abnLookupError,
		setAbnLookupError,
		isLookingUp,
		handleAbnChange,
		handleAbnCodeTypeChange,
		triggerKybLookup,

		// Assessment
		answers,
		setAnswers,
		questions,
		answered,
		allDone,
		risk,
		rc,
		flagged,
		grouped,

		// Live indicators
		liveRisk,
		liveRc,
		liveFlagged,
		liveSafe,
		liveUnanswered,

		// UI state
		activeSection,
		setActiveSection,
		hoveredInfo,
		setHoveredInfo,
		editingId,
		isAssessmentCompleted,
		isCompleting,

		// Actions
		reset,
		handleComplete,
		deleteHistoryEntry,
		handleLogout,
	};
}

export type RiskAssessmentState = ReturnType<typeof useRiskAssessmentState>;
